import { useCallback, useMemo, useState } from 'react';
import { Bell, Home, PenLine, Search, User } from 'lucide-react';
import type { GistChipOption, GistFeedReply, GistPostView, GistTab } from '@london-life/contracts';
import { PhoneAppFrame, useEscapeClose, type PhoneAppProps } from '../lib/phone-app';
import { useBackLayer } from '../lib/back-stack';
import { GistCompose } from './compose';
import { GistFeed } from './feed';
import { GistNotices } from './notices';
import { GistPostPage } from './post-view';
import { GistProfilePage } from './profile';
import { GistSearch } from './search';
import type { GistNav, GistPage } from './parts';

type Section = 'home' | 'search' | 'notices' | 'me';
const SECTIONS: { id: Section; label: string; icon: typeof Home }[] = [
  { id: 'home', label: 'Home', icon: Home },
  { id: 'search', label: 'Search', icon: Search },
  { id: 'notices', label: 'Notifications', icon: Bell },
  { id: 'me', label: 'Me', icon: User },
];
const HOME_TABS: { id: GistTab; label: string }[] = [
  { id: 'latest', label: 'For you' },
  { id: 'trending', label: 'Trending' },
  { id: 'following', label: 'Following' },
];
const HOME_EMPTY: Partial<Record<GistTab, { title: string; body: string }>> = {
  latest: { title: 'Nothing here yet', body: 'Be the first to say something.' },
  trending: { title: 'Nothing trending yet', body: 'Heart the posts you like!' },
  following: {
    title: 'Nothing from people you follow yet',
    body: 'Your contacts show here, and anyone you follow from their page.',
  },
};

// Gist, as Lagos Life's 📰 Gist in its X-style layout: Home (For you, Trending, Following), Search (Trending in
// London), Notifications (with their badge) and Me along the bottom, ✏️ to post; a post, a player's page and the
// composer open over them, and back closes the top one.
export function GistScreen(props: PhoneAppProps) {
  const [section, setSection] = useState<Section>('home');
  const [tab, setTab] = useState<GistTab>('latest');
  const [pages, setPages] = useState<GistPage[]>(() => (props.open ? [{ kind: 'post', id: props.open }] : []));
  const [q, setQ] = useState('');
  const [meta, setMeta] = useState<{ chips: GistChipOption[]; postsLeftToday: number; unread: number }>({
    chips: [],
    postsLeftToday: 1,
    unread: 0,
  });
  const [added, setAdded] = useState<GistPostView | null>(null);
  // the home feed reloads when its tab is tapped again
  const [round, setRound] = useState(0);
  useEscapeClose(props.onClose);
  const top = pages.at(-1) ?? null;
  const back = useCallback(() => setPages((now) => now.slice(0, -1)), []);
  useBackLayer(pages.length > 0, back);
  const nav = useMemo<GistNav>(
    () => ({
      post: (id, options) => setPages((now) => [...now, { kind: 'post', id, ...options }]),
      user: (id, name) => setPages((now) => [...now, { kind: 'user', id, name }]),
      search: (text) => {
        setPages([]);
        setQ(text);
        setSection('search');
      },
      compose: (append) => setPages((now) => [...now, { kind: 'compose', ...(append ? { append } : {}) }]),
      back,
    }),
    [back],
  );
  const onReply = useCallback(
    (reply: GistFeedReply) =>
      setMeta({ chips: reply.chips, postsLeftToday: reply.postsLeftToday, unread: reply.unread }),
    [],
  );
  const seen = useCallback(() => setMeta((now) => ({ ...now, unread: 0 })), []);
  const title =
    top?.kind === 'post'
      ? top.reply
        ? 'Reply'
        : 'Post'
      : top?.kind === 'user'
        ? top.name
        : top?.kind === 'compose'
          ? top.append
            ? 'Add to thread'
            : 'New post'
          : section === 'home'
            ? '📰 Gist'
            : SECTIONS.find((item) => item.id === section)!.label;
  return (
    <PhoneAppFrame
      id="gist"
      title={title}
      className="gist-screen"
      closeLabel="Close Gist"
      back={top ? { label: '‹ Back', onClick: back } : undefined}
      onClose={props.onClose}
      failure={null}
      onRetry={() => {}}
    >
      <div className="gist-shell">
        <div className="gist-scroll" hidden={top !== null}>
          {section === 'home' && (
            <>
              <div className="gist-tabs" role="tablist" aria-label="Gist">
                {HOME_TABS.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    role="tab"
                    aria-selected={tab === item.id}
                    className={tab === item.id ? 'active' : ''}
                    onClick={() => (item.id === tab ? setRound((n) => n + 1) : setTab(item.id))}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <GistFeed
                key={`${tab}:${round}`}
                tab={tab}
                nav={nav}
                empty={HOME_EMPTY[tab]!}
                onReply={onReply}
                added={added}
              />
            </>
          )}
          {section === 'search' && <GistSearch q={q} setQ={setQ} nav={nav} />}
          {section === 'notices' && <GistNotices nav={nav} onSeen={seen} />}
          {section === 'me' && <GistProfilePage lifeId={null} nav={nav} onReply={onReply} added={added} />}
        </div>
        {top && (
          <div className="gist-scroll gist-page" key={pages.length}>
            {top.kind === 'post' && <GistPostPage id={top.id} reply={top.reply} nav={nav} />}
            {top.kind === 'user' && (
              <GistProfilePage lifeId={top.id} nav={nav} onChat={props.onChat} onReply={onReply} />
            )}
            {top.kind === 'compose' && (
              <GistCompose
                chips={meta.chips}
                postsLeftToday={meta.postsLeftToday}
                append={top.append}
                onCancel={back}
                onDone={(post) => {
                  back();
                  if (post) {
                    setAdded(post);
                    setMeta((now) => ({ ...now, postsLeftToday: Math.max(0, now.postsLeftToday - 1) }));
                  }
                }}
              />
            )}
          </div>
        )}
        {top?.kind !== 'compose' && (
          <nav className="gist-nav" aria-label="Gist">
            {!top && (section === 'home' || section === 'me') && (
              <button type="button" className="gist-fab" aria-label="New post" onClick={() => nav.compose()}>
                <PenLine aria-hidden="true" />
              </button>
            )}
            {SECTIONS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                aria-label={id === 'notices' && meta.unread ? `${label}, ${meta.unread} new` : label}
                aria-current={!top && section === id ? 'page' : undefined}
                onClick={() => {
                  setPages([]);
                  if (id === section && id === 'home') setRound((n) => n + 1);
                  setSection(id);
                }}
              >
                <Icon aria-hidden="true" />
                {id === 'notices' && meta.unread > 0 && <b>{meta.unread > 99 ? '99+' : meta.unread}</b>}
              </button>
            ))}
          </nav>
        )}
      </div>
    </PhoneAppFrame>
  );
}
